import { Box, Card, Stack, Typography, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, TextField, FormControlLabel, Switch, DialogActions } from '@mui/material';
import { ReactElement, useState } from 'react';
import {
    Datagrid, Filter, FunctionField, List,
    useNotify, useTranslate, Button, DateInput
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components';
import ErrorPage403 from 'src/pages/error/403';
import { EmptyContent } from 'src/components/empty-content';
import { Iconify } from 'src/components/iconify';
import IdField from 'src/pages/admin/components/IdField';
import XDateField from 'src/pages/admin/components/XDateField';
import XTextField from 'src/pages/admin/components/XTextField';
import XTextInput from 'src/pages/admin/components/XTextInput';
import FilterGroup from 'src/pages/admin/components/FilterGroup';
import { Resources } from '../../resource';
import CreateCdnUpload from './Create';
import { XDateInput } from 'src/pages/admin/components/XDateInput';
import { XDateTimeInput } from 'src/pages/admin/components/XDateTimeInput';

const ListFilter = (props: any) => (
  <Filter {...(props || {})}>
    <FilterGroup alwaysOn direction="row">
      <XTextInput source="filename" label="module.cdnUpload.filename" />
      <XTextInput source="cdnUrl" label="module.cdnUpload.cdnUrl" />
      <XTextInput source="uploadedBy" label="module.cdnUpload.uploadedBy" />
      <XDateTimeInput source="uploadedAt" label="module.cdnUpload.uploadedAt" />
    </FilterGroup>
  </Filter>
);

const CdnUploadList = () => {
    const t = useTranslate();
    const notify = useNotify();
    const [popup, setPopup] = useState<ReactElement>();
    type UploadedItem = { filename: string; url?: string; error?: string };
    const [uploadedItems, setUploadedItems] = useState<UploadedItem[]>([]);

    const handleCopy = (url: string) => {
        navigator.clipboard.writeText(url);
        notify('module.cdnUpload.copySuccess', { type: 'info' });
    };

    const handleCopyAll = () => {
        const urls = uploadedItems.filter((i) => !i.error && i.url).map((i) => i.url!).join('\n');
        navigator.clipboard.writeText(urls);
        notify('module.cdnUpload.copyAllSuccess', { type: 'info' });
    };

    return (
        <PermissionGuard resource="cdn-uploads" action="read" fallback={<ErrorPage403 />}>
            <DashboardContent>
                <CustomBreadcrumbs
                    heading={t('module.cdnUpload.title')}
                    links={[{ name: t('module.cdnUpload.title'), href: `/${Resources.cdnUpload}` }]}
                    action={
                        <PermissionGuard resource="cdn-uploads" action="create" fallback={<></>}>
                            <Button
                                variant="contained"
                                size="medium"
                                onClick={() => {
                                    setUploadedItems([]);
                                    setPopup(
                                        <CreateCdnUpload
                                            onClose={() => setPopup(undefined)}
                                            onSuccess={() => {}}
                                            onFileUploaded={(item) => setUploadedItems((prev) => [...prev, item])}
                                        />
                                    );
                                }}
                                label={t('module.cdnUpload.uploadFile')}
                            >
                                <Iconify icon="eva:cloud-upload-fill" />
                            </Button>
                        </PermissionGuard>
                    }
                />

                {uploadedItems.length > 0 && (
                    <Card sx={{ mt: 2, p: 2 }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                            <Typography variant="subtitle2">{t('module.cdnUpload.uploadedList')}</Typography>
                            <Tooltip title={t('module.cdnUpload.copyAll')}>
                                <IconButton size="small" onClick={handleCopyAll}>
                                    <Iconify icon="eva:copy-outline" />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <Stack spacing={1} sx={{ maxHeight: 240, overflowY: 'auto' }}>
                            {uploadedItems.map((item, index) => (
                                <Stack key={index} direction="row" alignItems="center" spacing={1}>
                                    {item.error ? (
                                        <Iconify icon="eva:close-circle-fill" sx={{ color: 'error.main', flexShrink: 0 }} />
                                    ) : (
                                        <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: 'success.main', flexShrink: 0 }} />
                                    )}
                                    <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                                        {item.error
                                            ? item.filename
                                            : `${item.filename} — ${item.url}`}
                                    </Typography>
                                    {!item.error && item.url && (
                                        <Tooltip title={t('module.cdnUpload.copyLink')}>
                                            <IconButton size="small" onClick={() => handleCopy(item.url!)}>
                                                <Iconify icon="eva:copy-fill" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </Stack>
                            ))}
                        </Stack>
                    </Card>
                )}

                <Card sx={{ mt: 3, pb: 2 }}>
                    <List
                        resource={Resources.cdnUpload}
                        perPage={25}
                        filters={<ListFilter />}
                        empty={false}
                        sort={{ field: 'uploadedAt', order: 'DESC' }}
                    >
                        <Box sx={{ overflow: 'auto' }}>
                            <Datagrid rowClick={false} empty={<EmptyContent />} bulkActionButtons={false}>
                                <FunctionField label="ID" render={(r: any) => <IdField id={r.id} />} />
                                <FunctionField
                                    source="filename"
                                    label={t('module.cdnUpload.filename')}
                                    render={(r: any) => <XTextField text={r.filename} sx={{ width: '200px' }} />}
                                />
                                <FunctionField
                                    source="fileType"
                                    label={t('module.cdnUpload.fileType')}
                                    render={(r: any) => <XTextField text={r.fileType} sx={{ width: '100px' }} />}
                                />
                                <FunctionField
                                    source="size"
                                    label={t('module.cdnUpload.size')}
                                    render={(r: any) => (
                                        <XTextField
                                            text={r.size ? `${(r.size / 1024).toFixed(1)} KB` : '-'}
                                            sx={{ width: '90px' }}
                                        />
                                    )}
                                />
                                <FunctionField
                                    source="cdnUrl"
                                    label={t('module.cdnUpload.cdnUrl')}
                                    render={(r: any) => (
                                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '280px' }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}
                                            >
                                                {r.cdnUrl}
                                            </Typography>
                                            <Tooltip title={t('module.cdnUpload.copyLink')}>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => { e.stopPropagation(); handleCopy(r.cdnUrl); }}
                                                >
                                                    <Iconify icon="eva:copy-fill" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={t('module.cdnUpload.openLink')}>
                                                <IconButton
                                                    size="small"
                                                    component="a"
                                                    href={r.cdnUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                >
                                                    <Iconify icon="eva:external-link-fill" />
                                                </IconButton>
                                            </Tooltip>
                                        </Stack>
                                    )}
                                />
                                <FunctionField
                                    source="uploadedBy"
                                    label={t('module.cdnUpload.uploadedBy')}
                                    render={(r: any) => <XTextField text={r.uploadedBy} emptyBox />}
                                />
                                <FunctionField
                                    source="uploadedAt"
                                    label={t('module.cdnUpload.uploadedAt')}
                                    render={(r: any) => <XDateField value={r.uploadedAt} fullDate emptyBox />}
                                />
                            </Datagrid>
                        </Box>
                    </List>
                </Card>
                <Dialog open={Boolean(popup)} onClose={() => setPopup(undefined)} maxWidth="sm" fullWidth>
                    {popup}
                </Dialog>
            </DashboardContent>
        </PermissionGuard>
    );
};

export default CdnUploadList;
